import type { NumberFormatOptions } from 'next-intl';

import { type DateOnlyString, formatDateForDisplay } from '@/lib/utils/date-only';

import { ASSUMPTIONS } from '../constants';
import type { AssumptionKey, FoodWasteGranularity } from '../types';
import { massForDisplay, roundForDisplay } from './calc';

/**
 * A locale-bound number formatter: `useFormatter().number` in components,
 * `getFormatter({ locale }).number` on the server (PDF).
 */
export type NumberFormatter = (value: number, options?: NumberFormatOptions) => string;

export function formatWhole(formatNumber: NumberFormatter, value: number): string {
  return formatNumber(roundForDisplay(value), { maximumFractionDigits: 0 });
}

export function formatEur(formatNumber: NumberFormatter, value: number): string {
  return formatNumber(roundForDisplay(value), {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/** Number part of a mass (CO2e, food) plus which message (kg or t) it belongs in. */
export function formatMass(
  formatNumber: NumberFormatter,
  massKg: number,
): { unit: 'kg' | 't'; value: string } {
  const display = massForDisplay(massKg);
  const decimals = display.unit === 't' ? 1 : 0;
  return {
    unit: display.unit,
    value: formatNumber(display.value, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }),
  };
}

/** An assumption with the decimals its input shows, without grouping (for inputs). */
export function formatAssumption(
  formatNumber: NumberFormatter,
  key: AssumptionKey,
  value: number,
): string {
  const { decimals } = ASSUMPTIONS[key];
  return formatNumber(value, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: false,
  });
}

/** Week buckets by their first day ("29 Sep"), months by month and year. */
export function formatBucketLabel(
  locale: string,
  start: DateOnlyString,
  granularity: FoodWasteGranularity,
  variant: 'short' | 'long' = 'long',
): string {
  if (granularity === 'week') {
    return formatDateForDisplay(start, locale, { day: 'numeric', month: 'short' });
  }
  return formatDateForDisplay(start, locale, {
    month: 'short',
    year: variant === 'short' ? '2-digit' : 'numeric',
  });
}

export function formatDay(locale: string, date: DateOnlyString): string {
  return formatDateForDisplay(date, locale, { day: 'numeric', month: 'short', year: 'numeric' });
}
